import type { DamageType } from '../types/primitives'
import type { EnemyDefinitionV2, EnemySpawnSpec, ResolvedEnemyStats } from './types'
import { resolveEnemyLevelScale, resolveEnemyRankScale } from './scaling'

function mergeModifiers(
  a?: Partial<Record<DamageType, number>>,
  b?: Partial<Record<DamageType, number>>
): Partial<Record<DamageType, number>> {
  return { ...a, ...b }
}

/**
 * Pure enemy stat resolver.
 * enemyLevel=1 + NORMAL/BOSS rank → exact M41 catalog stats for ACTIVE content.
 */
export function resolveEnemyStats(
  definition: EnemyDefinitionV2,
  spawn: Pick<EnemySpawnSpec, 'enemyLevel' | 'enemyRank'>
): ResolvedEnemyStats {
  if (!Number.isFinite(spawn.enemyLevel) || spawn.enemyLevel < 1) {
    throw new Error(`Invalid enemyLevel: ${spawn.enemyLevel}`)
  }
  const levelScale = resolveEnemyLevelScale(spawn.enemyLevel)
  const rankScale = resolveEnemyRankScale(spawn.enemyRank)
  const base = definition.baseStats
  const armor = {
    ...definition.armorProfile,
    armorValue: definition.armorProfile.armorValue + rankScale.armorValueBonus
  }

  const maxHp = Math.round(base.maxHp * levelScale.hp * rankScale.hp)
  const reward = Math.round(
    definition.rewardProfile.killReward * levelScale.reward * rankScale.reward
  )

  const regen = definition.traits.regen
    ? {
        hpPerSecond: definition.traits.regen.hpPerSecond * levelScale.regenHpPerSecond,
        delayAfterDamage: definition.traits.regen.delayAfterDamage
      }
    : undefined

  const shield = definition.traits.shield
    ? {
        maxShield: Math.round(definition.traits.shield.maxShield * levelScale.shield)
      }
    : undefined

  return {
    enemyId: definition.id,
    enemyLevel: spawn.enemyLevel,
    enemyRank: spawn.enemyRank,
    movementClass: definition.movementClass,
    maxHp,
    speed: base.speed * levelScale.speed,
    leakDamage: base.leakDamage * levelScale.leakDamage,
    reward,
    radius: base.radius,
    color: base.color,
    armorProfile: armor,
    damageModifiers: mergeModifiers(armor.damageModifiers, definition.damageModifiers),
    regen,
    shield,
    immuneStatuses: [...(definition.traits.immuneStatuses ?? [])],
    immuneStatusCategories: [...(definition.traits.immuneStatusCategories ?? [])],
    statusDurationMultiplier: definition.traits.statusDurationMultiplier ?? 1,
    statusCategoryDurationMultipliers: {
      ...(definition.traits.statusCategoryDurationMultipliers ?? {})
    },
    visualId: definition.visualId
  }
}
